/* Toasts for every write, without every screen having to ask for one.
 *
 * Before this, two drawers told you a write had worked or failed and fifty other writes went quiet:
 * a supplier saved, a device deleted, a sync finished, or a request refused by SharePoint, with
 * nothing on screen to say which. Most screens do show an error somewhere inline, but inline errors
 * sit wherever the form put them, which is often scrolled out of sight.
 *
 * This listens to the mutation cache, so it covers every useMutation in the app, including ones
 * written next year:
 *
 *   - a failure always toasts, with the service's own words;
 *   - a success toasts only when the mutation says what happened, `meta: { ok: 'Device saved' }`.
 *     Guessing a sentence for an unnamed write is how a toast ends up saying "Success";
 *   - `meta: { quiet: true }` opts out, for the two drawers whose writes already report themselves
 *     through their own long-lived toast and would otherwise say everything twice.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './ui';

interface MutationMeta {
  ok?: string;
  quiet?: boolean;
}

export function messageOf(error: unknown): string {
  const text = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return text.trim() || 'That did not work, and no reason came back.';
}

export function ToastBridge(): null {
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    return qc.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const meta = (event.mutation.meta ?? {}) as MutationMeta;
      if (meta.quiet) return;
      const action = event.action;
      if (action.type === 'error') {
        toast(messageOf(action.error), 'error');
      } else if (action.type === 'success' && meta.ok) {
        toast(meta.ok, 'ok');
      }
    });
  }, [qc, toast]);

  return null;
}
